// The Experiment tab's own backend system (padihat-backend padihat/experiment/, mounted at /api/padihat/experiment/):
// built-in presets, calibration campaigns, the calibration what-if and a device's Calibration offset. Saved (user)
// presets stay on greenhouse/profiles/ (StressProfile), now with a `kind`.
import { requestBlob, requestJson } from "services/http";

const BASE = "/api/padihat/experiment";
const enc = encodeURIComponent;
const jsonOptions = (method, body) => ({
  method,
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body ?? {}),
});
const numericId = (id) => {
  const value = Number(id);
  if (!Number.isInteger(value) || value <= 0) throw new Error(`Invalid id: ${id}`);
  return value;
};

export const getExperimentPresets = () => requestJson(`${BASE}/presets/`);

export const estimatePlan = ({ conditions, ladder, stability, sensorIntervalS }) =>
  requestJson(
    `${BASE}/estimate/`,
    jsonOptions("POST", { conditions, ladder: ladder || {}, stability: stability || {}, sensor_interval_s: sensorIntervalS ?? null })
  );

export const getCampaignStatus = (controllerMac, deviceMac) => {
  const params = new URLSearchParams();
  if (deviceMac) params.set("device", deviceMac);
  return requestJson(`${BASE}/controllers/${enc(controllerMac)}/campaigns/status/?${params.toString()}`);
};

export const listCampaigns = (controllerMac) => requestJson(`${BASE}/controllers/${enc(controllerMac)}/campaigns/`);

export const startCampaign = (controllerMac, { deviceMac, name, notes, presetKey, profileId, conditions, ladder, stability }) =>
  requestJson(
    `${BASE}/controllers/${enc(controllerMac)}/campaigns/`,
    jsonOptions("POST", {
      device_mac: deviceMac,
      name: name || "",
      notes: notes || "",
      preset_key: presetKey || "",
      profile_id: profileId || null,
      conditions,
      ladder: ladder || {},
      stability: stability || {},
    })
  );

export const getCampaign = (id) => requestJson(`${BASE}/campaigns/${numericId(id)}/`);

const control = (action) => (id) => requestJson(`${BASE}/campaigns/${numericId(id)}/${action}/`, jsonOptions("POST", {}));
export const pauseCampaign = control("pause");
export const resumeCampaign = control("resume");
export const stopCampaign = control("stop");
export const skipCampaignCondition = control("skip-condition");

export async function exportCampaign(id) {
  const { blob } = await requestBlob(`${BASE}/campaigns/${numericId(id)}/export/`);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `calibration-campaign-${numericId(id)}.zip`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// source: {campaign: id} or {run, controller, device?}; options: {trainCount, split, series ("est" | "cal"; default:
// the backend's choice, our estimate when it has enough pairs)}
export const getWhatIf = (source, { trainCount, split, series } = {}) => {
  const params = new URLSearchParams();
  if (source.campaign) params.set("campaign", String(numericId(source.campaign)));
  else {
    params.set("run", source.run);
    if (source.controller) params.set("controller", source.controller);
    if (source.device) params.set("device", source.device);
    if (trainCount) params.set("train_count", String(trainCount));
    if (split) params.set("split", split);
  }
  if (series) params.set("series", series);
  return requestJson(`${BASE}/whatif/?${params.toString()}`);
};

export const getCalibrationOffset = (deviceMac) => requestJson(`${BASE}/devices/${enc(deviceMac)}/calibration-offset/`);

// series: the level the method was fitted on (the what-if's `source.series`), so the offset matches what was shown.
export const applyCalibrationOffset = ({ source, method, series, expected, note }) =>
  requestJson(
    `${BASE}/calibration-offset/apply/`,
    jsonOptions("POST", {
      ...(source.campaign ? { campaign: source.campaign } : { run: source.run, controller: source.controller, device: source.device }),
      method,
      ...(series ? { series } : {}),
      expected: expected ?? null,
      note: note || "",
    })
  );

// offsetCm null clears the calibration offset.
export const setManualCalibrationOffset = (deviceMac, { offsetCm, expected, note }) =>
  requestJson(
    `${BASE}/devices/${enc(deviceMac)}/calibration-offset/`,
    jsonOptions("POST", { offset_cm: offsetCm, expected: expected ?? null, note: note || "" })
  );

export const revertCalibrationOffset = (revisionId) =>
  requestJson(`${BASE}/calibration-offset/revisions/${numericId(revisionId)}/revert/`, jsonOptions("POST", {}));

// Saved calibration presets live on greenhouse/profiles/ with kind "calibration".
export const createCalibrationPreset = ({ name, description, conditions, ladder, stability }) =>
  requestJson(
    "/api/padihat/greenhouse/profiles/",
    jsonOptions("POST", { kind: "calibration", name, description: description || "", conditions, ladder: ladder || {}, stability: stability || {} })
  );

export const updateCalibrationPreset = (id, { name, description, conditions, ladder, stability }) =>
  requestJson(
    `/api/padihat/greenhouse/profiles/${numericId(id)}/`,
    jsonOptions("PATCH", { name, description, conditions, ladder, stability })
  );

// A radar node's radar zero (cm below the pipe opening; level = pipe opening to ground - radar zero - distance).
export const getRadarZero = (deviceMac) => requestJson(`${BASE}/devices/${enc(deviceMac)}/radar-zero/`);

// radarZeroCm null clears it; expected is the radar zero the caller last saw (null if none).
export const setRadarZero = (deviceMac, { radarZeroCm, expected, note }) =>
  requestJson(
    `${BASE}/devices/${enc(deviceMac)}/radar-zero/`,
    jsonOptions("POST", { radar_zero_cm: radarZeroCm, expected: expected ?? null, note: note || "" })
  );

export const revertRadarZero = (revisionId) =>
  requestJson(`${BASE}/radar-zero/revisions/${numericId(revisionId)}/revert/`, jsonOptions("POST", {}));

// The radar zero a campaign finds; pipeOpeningCm only for campaigns from before the opening was recorded.
export const getCampaignRadarZero = (campaignId, { pipeOpeningCm } = {}) => {
  const params = new URLSearchParams();
  if (pipeOpeningCm) params.set("pipe_opening_cm", String(pipeOpeningCm));
  const query = params.toString();
  return requestJson(`${BASE}/campaigns/${numericId(campaignId)}/radar-zero/${query ? `?${query}` : ""}`);
};

export const applyCampaignRadarZero = (campaignId, { expected, note, pipeOpeningCm }) =>
  requestJson(
    `${BASE}/campaigns/${numericId(campaignId)}/radar-zero/apply/`,
    jsonOptions("POST", { expected: expected ?? null, note: note || "", ...(pipeOpeningCm ? { pipe_opening_cm: pipeOpeningCm } : {}) })
  );
